import { z } from "zod";
import { emailSchema } from "./common.js";

export const FORWARD_PROVIDERS = ["gmail", "yahoo", "cloudflare"] as const;
export type ForwardProvider = (typeof FORWARD_PROVIDERS)[number];

/** A provider's "confirm this forward" email, captured by `screen` for the setup screen. */
export const forwardConfirmationSchema = z.strictObject({
  provider: z.enum(FORWARD_PROVIDERS),
  code: z.string().max(40).optional(),
  link: z.url().max(2000).optional(),
  at: z.iso.datetime(),
});
export type ForwardConfirmation = z.infer<typeof forwardConfirmationSchema>;

/** `shared` sends from the mailhand reply domain. An address sends from a ready sending domain. */
export const sendFromSchema = z.union([z.literal("shared"), emailSchema]);
export type SendFrom = z.infer<typeof sendFromSchema>;

export const displayNameSchema = z.string().trim().min(1).max(80);

export const inboxSchema = z.strictObject({
  address: emailSchema,
  displayName: displayNameSchema,
  replyTo: emailSchema,
  sendFrom: sendFromSchema,
  copyRepliesTo: emailSchema.nullable(),
  forwardConfirmation: forwardConfirmationSchema.optional(),
  createdAt: z.iso.datetime(),
});
export type Inbox = z.infer<typeof inboxSchema>;

export const inboxCreateSchema = z.strictObject({
  displayName: displayNameSchema,
  replyTo: emailSchema,
});
export type InboxCreate = z.infer<typeof inboxCreateSchema>;

export const inboxPatchSchema = z
  .strictObject({
    displayName: displayNameSchema.optional(),
    replyTo: emailSchema.optional(),
    sendFrom: sendFromSchema.optional(),
    copyRepliesTo: emailSchema.nullable().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "Send at least one field" });
export type InboxPatch = z.infer<typeof inboxPatchSchema>;

/** `pending_challenge`: waiting for the TXT record. `pending_dns`: waiting for SES to see the DKIM records. */
export const DOMAIN_STATES = ["pending_challenge", "pending_dns", "ready"] as const;
export type DomainState = (typeof DOMAIN_STATES)[number];

export const domainNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "Not a domain name");

export const dnsRecordSchema = z.strictObject({
  type: z.enum(["TXT", "CNAME"]),
  name: z.string(),
  value: z.string(),
});
export type DnsRecord = z.infer<typeof dnsRecordSchema>;

export const sendingDomainFields = {
  domain: domainNameSchema,
  dnsRecords: z.array(dnsRecordSchema),
  createdAt: z.iso.datetime(),
};

export const sendingDomainSchema = z.discriminatedUnion("state", [
  z.strictObject({ ...sendingDomainFields, state: z.literal("pending_challenge"), challengeExpiresAt: z.iso.datetime() }),
  z.strictObject({ ...sendingDomainFields, state: z.literal(["pending_dns", "ready"]) }),
]);
export type SendingDomain = z.infer<typeof sendingDomainSchema>;

export const domainCreateSchema = z.strictObject({ domain: domainNameSchema });
